import type { Entity } from '@backstage/catalog-model';

/**
 * What the Create page reads off a template entity (DX-650). The entity's spec
 * is the template file's, untyped in the catalog model, so each read is
 * defensive: a template the catalog holds may be one nobody here wrote.
 */

type Step = { id?: string; name?: string; action?: string };

/** The names of the steps a run takes, in order. */
export const stepNamesOf = (template?: Entity): string[] =>
  ((template?.spec?.steps ?? []) as Step[]).map(
    step => step.name ?? step.id ?? step.action ?? 'Step',
  );

/** The template's title, falling back to its name as the catalog does. */
export const titleOf = (template: Entity): string =>
  template.metadata.title ?? template.metadata.name;

/**
 * The parts of a repository picker's value, `host?owner=…&repo=…` — the shape
 * the engine's RepoUrlPicker writes, with `organization` where a host has one
 * instead of an owner.
 */
export const repoLocationOf = (value?: string) => {
  const [host = '', query = ''] = (value ?? '').split('?');
  const params = new URLSearchParams(query);
  return {
    host,
    owner: params.get('owner') ?? params.get('organization') ?? '',
    repo: params.get('repo') ?? '',
  };
};

type Schema = Record<string, any>;

/**
 * A field's definition anywhere in a page of the form: among the page's own
 * fields, or behind a condition (`dependencies`, `oneOf`, `allOf`, `if`/`then`)
 * — where a template asks a question only once another has a given answer.
 */
function definitionOf(schema: unknown, key: string): Schema | undefined {
  if (!schema || typeof schema !== 'object') return undefined;
  const node = schema as Schema;
  if (node.properties && typeof node.properties === 'object') {
    if (key in node.properties) return node.properties[key];
  }
  const conditions = [
    ...Object.values(node.dependencies ?? {}),
    ...[node.oneOf, node.anyOf, node.allOf].flatMap(list => list ?? []),
    node.then,
    node.else,
  ];
  for (const condition of conditions) {
    const found = definitionOf(condition, key);
    if (found) return found;
  }
  return undefined;
}

/** The `ui:` options a definition carries in itself. */
const uiOf = (definition: Schema) =>
  Object.fromEntries(
    Object.entries(definition).filter(([key]) => key.startsWith('ui:')),
  );

export type ReviewRow = {
  key: string;
  label: string;
  /** The field's definition, for the names its choices are shown by. */
  definition: Schema;
  ui: Schema;
};

type ReviewedPage = {
  schema: { properties?: unknown };
  mergedSchema: unknown;
  uiSchema?: Schema;
};

/**
 * What the review lists, as the template engine's own review does: every field
 * the pages ask, in their order, then any other value the form holds — a field
 * behind a condition, or one handed in through the address — each under the
 * label its definition gives it. Fields a template hides from its review are
 * left out.
 */
export function reviewRows(
  pages: ReviewedPage[],
  values: Record<string, unknown>,
): ReviewRow[] {
  const keys = new Set([
    ...pages.flatMap(page =>
      Object.keys((page.schema.properties as Schema | undefined) ?? {}),
    ),
    ...Object.keys(values),
  ]);
  return [...keys].flatMap(key => {
    let definition: Schema = {};
    let ui: Schema = {};
    for (const page of pages) {
      const found = definitionOf(page.mergedSchema, key);
      if (found) {
        definition = found;
        ui = { ...uiOf(found), ...(page.uiSchema?.[key] ?? {}) };
        break;
      }
    }
    const review = ui['ui:backstage']?.review ?? {};
    if (ui['ui:widget'] === 'hidden' || review.show === false) return [];
    return [
      {
        key,
        label: review.name ?? definition.title ?? key,
        definition,
        ui,
      },
    ];
  });
}
